import { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import Tippy from '@tippyjs/react';

import { practice_kinds, practice_status, tax_id_types } from 'lib/constants/constants';
import { useStepperContext } from 'lib/context/StepperContext/StepperContext';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';

import usePracticeMainDetialsFormik from './lib/mainDetailsFormik';

const MainDetailsForm = ({ mutateOrchestratorData, orchestratorLoading }) => {
  const { isAdmin, setHeaderButtonProps, addFooterActions, goNext } = useStepperContext();
  const { practice } = useOutletContext();

  const initialValues = {
    practiceName: practice?.name ?? '',
    dbaName: practice?.dba_name ?? '',
    taxIdType: practice?.tax_id_type ?? '',
    taxId: practice?.tax_id ?? '',
    taxonomy: practice?.taxonomy ?? '',
    practiceKind: practice?.kind,
    ...(isAdmin && { practiceStatus: practice?.status })
  };

  const formik = usePracticeMainDetialsFormik(initialValues, async (values) => {
    // Remove practiceStatus from submission if user is not admin
    const submitValues = isAdmin ? values : { ...values };
    if (!isAdmin) {
      delete submitValues.practiceStatus;
    }
    return mutateOrchestratorData(submitValues);
  });

  const handleSubmit = () => {
    if (formik.dirty) {
      formik.handleSubmit();
    } else {
      goNext();
    }
  };

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick: handleSubmit,
        isDisabled: orchestratorLoading || (formik.dirty && !formik.isValid)
      }
    ]);
  }, [formik.isValid, formik.dirty, orchestratorLoading]);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 px-0.5 sm:grid-cols-1 md:grid-cols-2">
        <Input
          data-qa="practice-name"
          label="Practice name"
          name="practiceName"
          value={formik.values.practiceName}
          onChange={formik.handleChange}
          error={formik.touched.practiceName && formik.errors.practiceName}
          additionalComponents={
            <Tippy
              content={<span>Legal name must include suffix unless sole proprietor</span>}
              className="tippy-dark"
              arrow>
              <div className="flex">
                <Icon icon="info-mono" />
              </div>
            </Tippy>
          }
        />
        <Input
          data-qa="practice-dba-name"
          label={<span className="normal-case">DBA name</span>}
          name="dbaName"
          placeholder="Enter DBA name"
          value={formik.values.dbaName}
          onChange={formik.handleChange}
          error={formik.touched.dbaName && formik.errors.dbaName}
          additionalComponents={
            <Tippy
              content={
                <span>
                  Doing business as (DBA) name will appear on receipts and patient facing items.
                </span>
              }
              className="tippy-dark"
              arrow>
              <div className="flex">
                <Icon icon="info-mono" />
              </div>
            </Tippy>
          }
        />
        <Input
          data-qa="practice-taxonomy"
          label="Taxonomy"
          name="taxonomy"
          placeholder="Enter taxonomy"
          value={formik.values.taxonomy}
          onChange={formik.handleChange}
          error={formik.touched.taxonomy && formik.errors.taxonomy}
        />
        <div className="hidden grid-cols-2 gap-3 lg:grid">
          <Select
            inputId="taxIdType"
            label={<span className="normal-case">Tax ID type</span>}
            options={tax_id_types}
            placeholder="Select tax ID type"
            onChange={(v) => formik.setFieldValue('taxIdType', v?.value)}
            value={tax_id_types.find((o) => o.value === formik.values.taxIdType) ?? null}
            isMulti={false}
          />
          <Input
            data-qa="practice-tax-id"
            label={<span className="normal-case">Tax ID</span>}
            name="taxId"
            placeholder="Enter tax ID"
            value={formik.values.taxId}
            onChange={formik.handleChange}
            error={formik.touched.taxId && formik.errors.taxId}
          />
        </div>
        <div className="lg:hidden">
          <Select
            inputId="taxIdTypeMobile"
            label={<span className="normal-case">Tax ID Type</span>}
            options={tax_id_types}
            placeholder="Select tax ID type"
            onChange={(v) => formik.setFieldValue('taxIdType', v?.value)}
            value={tax_id_types.find((o) => o.value === formik.values.taxIdType) ?? null}
            isMulti={false}
          />
        </div>
        <div className="lg:hidden">
          <Input
            data-qa="practice-tax-id-mobile"
            label={<span className="normal-case">Tax ID</span>}
            name="taxId"
            placeholder="Enter tax ID"
            value={formik.values.taxId}
            onChange={formik.handleChange}
            error={formik.touched.taxId && formik.errors.taxId}
          />
        </div>
        {isAdmin && (
          <Select
            inputId="practiceKind"
            label="Kind"
            options={practice_kinds}
            placeholder="Select practice kind"
            onChange={(v) => formik.setFieldValue('practiceKind', v?.value)}
            value={practice_kinds.find((o) => o.value === formik.values.practiceKind) ?? null}
            isMulti={false}
            isOptional
          />
        )}
        {isAdmin && (
          <Select
            label="Status"
            inputId="practiceStatus"
            options={practice_status}
            placeholder="Select status"
            onChange={(v) => formik.setFieldValue('practiceStatus', v?.value)}
            value={practice_status.find((o) => o.value === formik.values.practiceStatus) ?? null}
            isMulti={false}
          />
        )}
      </div>
    </>
  );
};

export default MainDetailsForm;
